import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Plot from 'react-plotly.js';
import WorkflowSidebar from './components/WorkflowSidebar';
import MethodCard from './components/MethodCard';
import CancelButton from './components/CancelButton';
import apiClient from '../../api/client';
import SaveResultsWizard from '../../components/chemistry/SaveResultsWizard';
import { useWorkbenchContext } from '../../hooks/useWorkbenchContext';
import WorkbenchContextBar from '../../components/workbench/WorkbenchContextBar';
import ToolSwitcher from '../../components/workbench/ToolSwitcher';
import { useToolStep } from '../../hooks/useWorkbenchContext';

const STEPS = [
  { name: 'Upload' },
  { name: 'Model' },
  { name: 'Fit' },
  { name: 'Review' },
];

const SPR_MODELS = [
  { value: '1to1', label: '1:1 Langmuir', desc: 'Simple bimolecular binding' },
  { value: 'heterogeneous', label: 'Heterogeneous Ligand', desc: 'Two independent binding sites' },
  { value: 'bivalent', label: 'Bivalent Analyte', desc: 'Analyte binds with two arms' },
  { value: 'two_state', label: 'Two-State Conformational Change', desc: 'Binding followed by conformational change' },
];

const SAMPLE_DATA = `0,0.0,5
5,2.3,5
10,4.4,5
20,8.1,5
30,10.5,5
60,15.1,5
90,18.0,5
120,18.9,5
150,19.5,5
180,19.8,5
200,13.3,5
210,11.2,5
230,7.5,5
260,4.0,5
300,1.6,5
0,0.0,25
5,11.3,25
10,20.5,25
20,33.0,25
30,41.1,25
60,52.0,25
90,54.2,25
120,55.2,25
150,55.6,25
180,55.8,25
200,37.2,25
210,30.6,25
230,20.5,25
260,11.4,25
300,4.7,25
0,0.2,100
5,37.1,100
10,57.4,100
20,75.6,100
30,80.8,100
60,83.5,100
90,83.5,100
120,83.0,100
150,83.6,100
180,83.0,100
200,55.8,100
210,45.6,100
230,30.7,100
260,17.1,100
300,7.8,100`;

const WorkbenchSPR: React.FC = () => {
  const { smiles: ctxSmiles, moleculeId: ctxMoleculeId, fileId: ctxFileId, projectId: ctxProjectId } = useWorkbenchContext();
  const [step, setStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [rawText, setRawText] = useState('');
  const [parsedPoints, setParsedPoints] = useState<{ t: number; r: number; c: number }[]>([]);
  const [parseError, setParseError] = useState('');
  const [model, setModel] = useState('1to1');
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');
  const abortRef = useRef<AbortController | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const { bailOutText, setHasSaved } = useToolStep();

  const parseData = (text: string) => {
    setParseError('');
    const lines = text.trim().split('\n').filter((l) => l.trim());
    const points: { t: number; r: number; c: number }[] = [];
    for (let idx = 0; idx < lines.length; idx++) {
      const parts = lines[idx].split(/[,\t;]+/).map((s) => s.trim());
      if (parts.length < 3) { if (idx === 0) continue; setParseError(`Line ${idx + 1}: need 3 columns (time, response, concentration)`); return; }
      const t = parseFloat(parts[0]), r = parseFloat(parts[1]), c = parseFloat(parts[2]);
      if (isNaN(t) || isNaN(r) || isNaN(c)) { if (idx === 0) continue; setParseError(`Line ${idx + 1}: non-numeric`); return; }
      points.push({ t, r, c });
    }
    if (points.length < 5) { setParseError('Need at least 5 data points'); return; }
    setParsedPoints(points);
    setCompletedSteps((prev) => Array.from(new Set([...prev, 0])));
    setStep(1);
  };

  const runFit = async () => {
    setIsRunning(true);
    setError('');
    setResult(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Backend requires all concentrations > 0; clamp zeros to near-zero
      const safePoints = parsedPoints.map((p) => ({ ...p, c: p.c > 0 ? p.c : 1e-15 }));
      const resp = await apiClient.post('/v2/kinetics/fit/spr', {
        points: safePoints,
        model,
      }, { signal: controller.signal });
      const data = resp.data;
      setResult(data);
      setCompletedSteps((prev) => Array.from(new Set([...prev, 2])));
      setStep(3);
    } catch (err: any) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') setError('Fit cancelled');
      else setError(err.response?.data?.detail || err.message || 'Fit failed');
    } finally {
      setIsRunning(false);
      abortRef.current = null;
    }
  };

  return (
    <>
      <WorkbenchContextBar bailOutText={bailOutText} />
      <div style={{ display: 'flex', height: 'calc(100vh - 84px)', background: 'var(--content-bg)' }}>
      <WorkflowSidebar steps={STEPS} currentStep={step} completedSteps={completedSteps} onStepClick={setStep} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={statusBarStyle}>
          <Link to="/hubs" style={{ color: '#f59e0b', textDecoration: 'none' }}>Toolbox</Link>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <ToolSwitcher currentToolId="spr" />
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Engine: SciPy ODE</span>
          <span style={{ color: 'var(--card-border)' }}>|</span>
          <span>Step {step + 1}/{STEPS.length}</span>
          <div style={{ flex: 1 }} />
          <CancelButton onCancel={() => abortRef.current?.abort()} isRunning={isRunning} label="Stop Fit" />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', gap: 20 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {step === 0 && (
              <div>
                <h2 style={h2Style}>Paste sensorgram data</h2>
                <p style={descStyle}>CSV: time, response, concentration (one row per point)</p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                  <button onClick={() => setRawText(SAMPLE_DATA)} style={btnStyle}>Load Sample Data</button>
                  <label style={{ ...btnStyle, cursor: 'pointer' }}>
                    Upload File
                    <input type="file" accept=".csv,.txt" style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) f.text().then(setRawText); }} />
                  </label>
                </div>
                <textarea value={rawText} onChange={(e) => setRawText(e.target.value)}
                  placeholder="time,response,concentration&#10;0,0.1,10&#10;5,2.3,10&#10;..." style={textareaStyle} rows={14} />
                {parseError && <div style={errorStyle}>{parseError}</div>}
                <button onClick={() => parseData(rawText)} disabled={!rawText.trim()}
                  style={{ ...primaryBtnStyle, marginTop: 12, opacity: rawText.trim() ? 1 : 0.5 }}>Parse Data</button>
              </div>
            )}

            {step === 1 && (
              <div>
                <h2 style={h2Style}>Select Binding Model</h2>
                <p style={descStyle}>{parsedPoints.length} data points loaded. Choose a kinetic model for fitting.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {SPR_MODELS.map((m) => (
                    <button key={m.value} onClick={() => setModel(m.value)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                        background: model === m.value ? 'rgba(245,158,11,0.1)' : 'var(--card-bg)',
                        border: model === m.value ? '1px solid #f59e0b' : '1px solid var(--card-border)',
                        borderRadius: 10, cursor: 'pointer', textAlign: 'left', width: '100%',
                      }}>
                      <div style={{ width: 16, height: 16, borderRadius: '50%', border: model === m.value ? '5px solid #f59e0b' : '2px solid var(--text-secondary)', flexShrink: 0 }} />
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>{m.label}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>{m.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
                <button onClick={() => { setCompletedSteps((prev) => Array.from(new Set([...prev, 1]))); setStep(2); }}
                  style={{ ...primaryBtnStyle, marginTop: 16 }}>Continue with {SPR_MODELS.find((m) => m.value === model)?.label}</button>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 style={h2Style}>Run SPR Fit</h2>
                <p style={descStyle}>Model: {SPR_MODELS.find((m) => m.value === model)?.label} | {parsedPoints.length} points</p>
                {!isRunning && <button onClick={runFit} style={primaryBtnStyle}>Fit Sensorgram</button>}
                {isRunning && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={spinnerStyle} /><span style={{ color: '#f59e0b' }}>Fitting sensorgram...</span>
                  </div>
                )}
                {error && <div style={errorStyle}>{error}</div>}
              </div>
            )}

            {step === 3 && result && (
              <div>
                <h2 style={h2Style}>SPR Fit Results</h2>

                {/* Model badge */}
                {result.model && (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 16, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', marginBottom: 16, fontSize: 12, color: '#60a5fa', fontWeight: 600 }}>
                    Model: {SPR_MODELS.find((m) => m.value === result.model)?.label || result.model}
                  </div>
                )}

                {/* Parameter cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
                  {result.params && Object.entries(result.params as Record<string, number>).map(([key, value]) => (
                    <div key={key} style={resultCardStyle}>
                      <div style={resultLabelStyle}>{key}</div>
                      <div style={resultValueStyle}>
                        {typeof value === 'number' ? (value as number).toPrecision(4) : String(value)}
                      </div>
                    </div>
                  ))}
                  {result.r2 != null && (
                    <div style={resultCardStyle}>
                      <div style={resultLabelStyle}>R²</div>
                      <div style={resultValueStyle}>{(result.r2 as number).toFixed(6)}</div>
                    </div>
                  )}
                </div>

                {/* Sensorgram chart: observed + fitted */}
                {(() => {
                  // Handle both response shapes: {data: {t,r}} or {req: {x,y}} or {data: {x,y}}
                  const obsX = result.data?.t || result.data?.x || result.req?.x;
                  const obsY = result.data?.r || result.data?.y || result.req?.y;
                  const fitX = result.fit?.t || result.fit?.x;
                  const fitY = result.fit?.r || result.fit?.y;
                  if (obsX && obsY) {
                    return (
                      <div style={chartContainerStyle}>
                        <Plot
                          data={[
                            {
                              x: obsX, y: obsY,
                              type: 'scatter' as const, mode: 'markers' as const,
                              name: 'Observed', marker: { color: '#f59e0b', size: 5 },
                            },
                            ...(fitX && fitY ? [{
                              x: fitX, y: fitY,
                              type: 'scatter' as const, mode: 'lines' as const,
                              name: 'Fitted', line: { color: '#3b82f6', width: 2 },
                            }] : []),
                          ]}
                          layout={{
                            title: { text: 'Sensorgram', font: { color: '#f5f5f5', size: 14 } },
                            xaxis: { title: { text: 'Time (s)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                            yaxis: { title: { text: 'Response (RU)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                            paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                            legend: { font: { color: '#a7abb6' }, x: 0.7, y: 0.95 },
                            margin: { t: 40, r: 20, b: 50, l: 60 }, height: 360,
                          }}
                          config={{ displayModeBar: false, responsive: true }}
                          style={{ width: '100%' }}
                        />
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Residuals chart */}
                {result.residuals && (
                  <div style={chartContainerStyle}>
                    <Plot
                      data={[{
                        x: result.residuals.x || result.residuals.t,
                        y: result.residuals.y || result.residuals.r,
                        type: 'bar' as const, name: 'Residuals',
                        marker: { color: ((result.residuals.y || result.residuals.r) as number[]).map((v: number) => v >= 0 ? '#22c55e' : '#ef4444') },
                      }]}
                      layout={{
                        title: { text: 'Residuals', font: { color: '#f5f5f5', size: 14 } },
                        xaxis: { title: { text: 'Time (s)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' } },
                        yaxis: { title: { text: 'Residual (RU)', font: { color: '#a7abb6' } }, gridcolor: 'rgba(255,255,255,0.06)', tickfont: { color: '#a7abb6' }, zeroline: true, zerolinecolor: 'rgba(255,255,255,0.2)' },
                        paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                        margin: { t: 40, r: 20, b: 50, l: 60 }, height: 240, showlegend: false,
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: '100%' }}
                    />
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => {
                    const lines: string[] = [];
                    if (result.model) lines.push(`model: ${result.model}`);
                    if (result.params) Object.entries(result.params as Record<string, number>).forEach(([k, v]) => lines.push(`${k}: ${(v as number).toPrecision(6)}`));
                    if (result.r2 != null) lines.push(`R2: ${(result.r2 as number).toFixed(6)}`);
                    navigator.clipboard.writeText(lines.join('\n'));
                  }} style={btnStyle}>Copy Results</button>
                  <button onClick={() => {
                    const lines = ['parameter,value'];
                    if (result.model) lines.push(`model,${result.model}`);
                    if (result.params) Object.entries(result.params as Record<string, number>).forEach(([k, v]) => lines.push(`${k},${v}`));
                    if (result.r2 != null) lines.push(`R2,${result.r2}`);
                    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = 'spr_results.csv'; a.click(); URL.revokeObjectURL(url);
                  }} style={primaryBtnStyle}>Download CSV</button>
                  <button onClick={() => setSaveOpen(true)} style={btnStyle}>Save to Project</button>
                </div>
                <SaveResultsWizard isOpen={saveOpen} onClose={() => setSaveOpen(false)} rawData={result} toolName="SPR Kinetics" toolKey="spr" smiles={ctxSmiles} moleculeId={ctxMoleculeId} fileId={ctxFileId} projectId={ctxProjectId} />
              </div>
            )}
          </div>

          <div style={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <MethodCard engine="SciPy" title="1:1 Langmuir Binding"
              description="Simple bimolecular interaction. Analyte (A) binds immobilized ligand (B) to form complex (AB)."
              equation="dR/dt = ka × C × (Rmax - R) - kd × R"
              parameters={[
                { name: 'ka', desc: 'Association rate constant (1/Ms)' },
                { name: 'kd', desc: 'Dissociation rate constant (1/s)' },
                { name: 'KD', desc: 'Equilibrium dissociation constant (kd/ka)' },
                { name: 'Rmax', desc: 'Maximum binding capacity (RU)' },
              ]}
              reference="Karlsson et al. (1991) J Immunol Methods 145:229-240"
              source="scipy.integrate.odeint + scipy.optimize.curve_fit" />
            <MethodCard engine="SciPy" title="Two-State Conformational Change"
              description="Binding followed by conformational rearrangement: A + B ⇌ AB ⇌ AB*"
              equation="dR1/dt = ka1×C×(Rmax-R1-R2) - kd1×R1 - ka2×R1 + kd2×R2"
              reference="Morton et al. (1995) Anal Biochem 227:176-185"
              source="scipy.integrate.odeint" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

const statusBarStyle: React.CSSProperties = { padding: '8px 20px', background: 'var(--hover-bg)', borderBottom: '1px solid var(--card-border)', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 };
const h2Style: React.CSSProperties = { color: '#f5f5f5', fontSize: 18, fontWeight: 600, marginBottom: 12, marginTop: 0 };
const descStyle: React.CSSProperties = { color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 };
const btnStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const primaryBtnStyle: React.CSSProperties = { ...btnStyle, background: '#f59e0b', color: 'var(--content-bg)', border: '1px solid #f59e0b', fontWeight: 600 };
const textareaStyle: React.CSSProperties = { width: '100%', padding: 14, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 13, fontFamily: 'monospace', resize: 'vertical', outline: 'none', boxSizing: 'border-box' };
const errorStyle: React.CSSProperties = { marginTop: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, color: '#fb7185', fontSize: 13 };
const spinnerStyle: React.CSSProperties = { width: 20, height: 20, border: '3px solid var(--card-border)', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' };
const resultCardStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '12px 14px' };
const resultLabelStyle: React.CSSProperties = { color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const resultValueStyle: React.CSSProperties = { color: '#f59e0b', fontSize: 18, fontWeight: 700, fontFamily: 'monospace' };
const chartContainerStyle: React.CSSProperties = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 16, marginBottom: 16 };

export default WorkbenchSPR;
